'use client';

import { useConsent } from "@/lib/consent/use-consent";
import { Button, Modal, Switch } from "@heroui/react";
import { useEffect, useState } from "react";

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CookiePreferencesModal({ isOpen, onClose }: CookiePreferencesModalProps) {
  const { privacyState, setCustomConsent } = useConsent();
  const { analytics: savedAnalytics, advertising: savedAdvertising } = privacyState.cookieConsent;
  const [analytics, setAnalytics] = useState(savedAnalytics);
  const [advertising, setAdvertising] = useState(savedAdvertising);
  const [reloadPending, setReloadPending] = useState(false);

  // useConsent reads the stored choice after mount, so follow it into the toggles.
  useEffect(() => {
    setAnalytics(savedAnalytics);
    setAdvertising(savedAdvertising);
  }, [savedAnalytics, savedAdvertising]);

  // Tags already loaded keep running, and gtm.blocklist is read from the head
  // snippet, so a changed choice takes effect on a fresh page load. useConsent
  // writes the cookie in its own effect, which runs before this one.
  useEffect(() => {
    if (reloadPending) window.location.reload();
  }, [reloadPending]);

  const save = (choice: { analytics: boolean; advertising: boolean }) => {
    setCustomConsent(choice);
    if (choice.analytics !== savedAnalytics || choice.advertising !== savedAdvertising) {
      setReloadPending(true);
    } else {
      onClose();
    }
  };

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <Modal.Container size="lg">
        <Modal.Dialog className="bg-background">
        <div className="p-6" data-testid="cookie-preferences-modal">
          <h2 className="mb-4 text-xl font-semibold">Cookie Preferences</h2>
          <p className="mb-6 text-sm text-foreground-600">
            Analytics and advertising tags run unless you turn them off here. Your choice is saved in this browser.
          </p>

          <div className="space-y-4">
            <Switch isSelected isDisabled className="w-full">
              <Switch.Content>
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <div className="flex flex-col">
                  <span className="font-medium">Essential Cookies</span>
                  <span className="text-sm text-foreground-500">Required for the website to function properly</span>
                </div>
              </Switch.Content>
            </Switch>

            <Switch
              isSelected={analytics}
              onChange={setAnalytics}
              className="w-full"
              data-testid="cookie-analytics"
            >
              <Switch.Content>
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <div className="flex flex-col">
                  <span className="font-medium">Analytics Cookies</span>
                  <span className="text-sm text-foreground-500">Help us understand how visitors interact with the website</span>
                </div>
              </Switch.Content>
            </Switch>

            <Switch
              isSelected={advertising}
              onChange={setAdvertising}
              className="w-full"
              data-testid="cookie-advertising"
            >
              <Switch.Content>
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <div className="flex flex-col">
                  <span className="font-medium">Advertising Cookies</span>
                  <span className="text-sm text-foreground-500">Measure our ads and the calls and forms they bring in</span>
                </div>
              </Switch.Content>
            </Switch>
          </div>
        </div>

        <Modal.Footer>
          <Button
            variant="danger"
            data-testid="cookie-reject-all"
            onPress={() => save({ analytics: false, advertising: false })}
          >
            Reject All
          </Button>
          <Button
            data-testid="cookie-accept-all"
            onPress={() => save({ analytics: true, advertising: true })}
          >
            Accept All
          </Button>
          <Button
            variant="secondary"
            data-testid="cookie-save"
            onPress={() => save({ analytics, advertising })}
          >
            Save Preferences
          </Button>
        </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
